import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconRefresh, IconUpload } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DataTableShell,
  useInfinitePage,
  FilterBar,
  PageHeader,
  SortableTh,
  StatusBadge,
  useTableSort,
} from "../../components/ui";
import { adminApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { formatDate, SEGMENT_LABELS } from "../../lib/format";
import type { JobStatus } from "../../types/api";

const ADMIN_JOB_TABS = [
  { value: "pending_review", label: "Pending review" },
  { value: "open", label: "Open" },
  { value: "bulk_upload", label: "Bulk upload" },
] as const;

type JobsTab = (typeof ADMIN_JOB_TABS)[number]["value"];

const ADMIN_JOB_TAB_DEFAULT: JobsTab = "pending_review";
const ADMIN_JOB_FILTER_DEFAULTS = { tab: ADMIN_JOB_TAB_DEFAULT };
const ADMIN_JOB_FILTER_ALLOWED = { tab: ADMIN_JOB_TABS.map((item) => item.value) };
const ADMIN_JOB_FILTER_OPTIONS = { allowed: ADMIN_JOB_FILTER_ALLOWED };

const CONSENT_BASIS_MIN_LENGTH = 8;
const DEFAULT_CONSENT_BASIS = "naukri_export_admin_attested";
const IMPORT_ERROR_PREVIEW = 8;

export function AdminJobsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const jobFilters = useQueryFilters(ADMIN_JOB_FILTER_DEFAULTS, ADMIN_JOB_FILTER_OPTIONS);
  const tab = jobFilters.values.tab as JobsTab;
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [consentBasis, setConsentBasis] = useState(DEFAULT_CONSENT_BASIS);

  const statusFilter: JobStatus = tab === "bulk_upload" ? "open" : tab;
  const jobsQuery = useInfinitePage(["admin-jobs", statusFilter], (page, pageSize) =>
    adminApi.listJobs({ status: statusFilter, page, page_size: pageSize }),
  );

  const backfillAllMutation = useMutation({
    mutationFn: () => adminApi.backfillAllOpenJobs(),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        message: `Scanned ${res.jobs_scanned} open job(s) · created ${res.matches_created} match(es)`,
      });
      void queryClient.invalidateQueries({ queryKey: ["admin-jobs"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Backfill failed",
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      adminApi.importCandidates(importFile!, {
        consent_basis: consentBasis.trim(),
        job_id: importJobId || undefined,
      }),
    onSuccess: (res) => {
      notifications.show({
        color: res.error_count ? "yellow" : "green",
        title: "Import finished",
        message: `${res.success_count} imported · ${res.error_count} row error(s)`,
      });
      setImportFile(null);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof Error ? err.message : "Import failed",
      });
    },
  });

  const jobs = jobsQuery.items;
  const { sorted: sortedJobs, sortKey, direction, toggle } = useTableSort(jobs, {
    title: (r) => r.title,
    company: (r) => r.company_name,
    segment: (r) => SEGMENT_LABELS[r.segment] ?? r.segment,
    openings: (r) => (r.openings_total ? r.openings_filled / r.openings_total : 0),
    status: (r) => r.status,
    created: (r) => r.published_at ?? r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.jobs}
        title="Jobs"
        subtitle="Open a job to approve fees, edit skills, publish insights, or backfill matches."
        actions={
          tab === "open" ? (
            <Button
              variant="default"
              leftSection={<IconRefresh size="var(--axio-unit-16)" />}
              loading={backfillAllMutation.isPending}
              onClick={() => backfillAllMutation.mutate()}
            >
              Run backfill for all open jobs
            </Button>
          ) : undefined
        }
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && jobFilters.set({ tab: value })}>
          <Tabs.List>
            {ADMIN_JOB_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      {tab === "bulk_upload" ? (
        <Stack gap="md">
          <Alert color="teal" title="Naukri / Apna / Sheets">
            Download the Excel template, paste names from your portal export, then upload. CTC
            columns are <strong>lakhs per year</strong> (type 9 for ₹9 LPA, not 90000). Every
            imported person is marked consent-given because you attest a consent basis — they will
            show in Explore. Only upload people you are allowed to call.
          </Alert>
          <Group>
            <Button
              variant="default"
              leftSection={<IconDownload size="var(--axio-unit-16)" />}
              onClick={() => void adminApi.downloadCandidateImportTemplate("xlsx")}
            >
              Download Excel template
            </Button>
            <Button
              variant="subtle"
              onClick={() => void adminApi.downloadCandidateImportTemplate("csv")}
            >
              CSV for Google Sheets
            </Button>
          </Group>
          <TextInput
            label="Consent basis"
            description="Required. Recorded on every new row (e.g. naukri_export_admin_attested)."
            required
            value={consentBasis}
            onChange={(e) => setConsentBasis(e.currentTarget.value)}
          />
          <Select
            label="Attach to job (optional)"
            description="Puts them on that job's Explore queue as inbound. Also fill segments in the sheet to match other open jobs."
            clearable
            searchable
            placeholder="No specific job"
            data={jobs.map((job) => ({
              value: job.id,
              label: `${job.title} · ${job.company_name}`,
            }))}
            value={importJobId}
            onChange={setImportJobId}
          />
          <FileInput
            label="Filled sheet"
            placeholder="CSV or .xlsx"
            accept=".csv,.xlsx,.xlsm"
            value={importFile}
            onChange={setImportFile}
          />
          <Button
            leftSection={<IconUpload size="var(--axio-unit-16)" />}
            loading={importMutation.isPending}
            disabled={!importFile || consentBasis.trim().length < CONSENT_BASIS_MIN_LENGTH}
            onClick={() => importMutation.mutate()}
          >
            Upload to Explore pool
          </Button>
          {importMutation.data && importMutation.data.errors.length > 0 ? (
            <Alert color="yellow" title={`${importMutation.data.error_count} row(s) failed`}>
              {importMutation.data.errors.slice(0, IMPORT_ERROR_PREVIEW).map((err) => (
                <Text key={`${err.row}-${err.error}`} size="sm">
                  Row {err.row}: {err.error}
                </Text>
              ))}
            </Alert>
          ) : null}
        </Stack>
      ) : (
        <DataTableShell
          loading={jobsQuery.isLoading}
          error={
            jobsQuery.isError
              ? jobsQuery.error instanceof Error
                ? jobsQuery.error.message
                : undefined
              : undefined
          }
          onRetry={() => void jobsQuery.refetch()}
          hasMore={jobsQuery.hasMore}
          loadingMore={jobsQuery.loadingMore}
          onLoadMore={jobsQuery.loadMore}
          isEmpty={jobs.length === 0}
          empty={{
            icon: NAV_ICONS.jobs,
            title: tab === "pending_review" ? "No jobs awaiting fee approval" : "No open jobs right now",
            description:
              tab === "pending_review"
                ? "Jobs submitted by HR will appear here for fee split review."
                : "Approved jobs will appear here once fee splits are confirmed.",
          }}
        >
          <Table verticalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="title" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Title
                </SortableTh>
                <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Company
                </SortableTh>
                <SortableTh columnKey="segment" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Segment
                </SortableTh>
                <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Status
                </SortableTh>
                <SortableTh columnKey="openings" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Openings
                </SortableTh>
                <SortableTh columnKey="created" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Date
                </SortableTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedJobs.map((job) => (
                <Table.Tr
                  key={job.id}
                  onClick={() => navigate(`/admin/jobs/${job.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <Table.Td fw="var(--axio-font-weight-medium)">
                    <Text
                      component={Link}
                      to={`/admin/jobs/${job.id}`}
                      fw="var(--axio-font-weight-medium)"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {job.title}
                    </Text>
                  </Table.Td>
                  <Table.Td>{job.company_name}</Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="outline" color="gray">
                      {SEGMENT_LABELS[job.segment] ?? job.segment}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={job.status} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" className="tabular">
                      {job.openings_filled}/{job.openings_total}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatDate(job.published_at ?? job.created_at)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      )}
    </Stack>
  );
}
